import { cn, useIntl } from '@finn/ui-utils';
import { ReactNode, useEffect, useState } from 'react';

import { GenericVehicleDetails } from '../../core/types';
import { getConfigLineLabel, useProductLabel } from '../../product-cards';
import { useSubscriptionPrice } from '../../product-pricing';
import { PDPTracking } from '../tracking/PDPTracking';

type Props = {
  className?: string;
  position?: 'top' | 'bottom';
  isAvailable: boolean;
  show: boolean;
  infoLines?: string[];
  vehicle: GenericVehicleDetails;
  renderSubmitButton(): ReactNode;
  closedUserGroupId?: string;
};

export const StickyBar = ({
  vehicle,
  className,
  isAvailable,
  renderSubmitButton,
  show,
  position = 'top',
  closedUserGroupId,
}: Props) => {
  const [loaded, setLoaded] = useState(false);

  // Add a bottom padding to prevent StickyBar from overlapping the footer
  useEffect(() => {
    if (position === 'bottom') {
      document.body.style.paddingBottom = '88px';

      return () => {
        document.body.style.paddingBottom = '';
      };
    }
  }, [position]);

  const i18n = useIntl();
  const configLine = getConfigLineLabel(vehicle);

  const { partnerDiscount, originalPriceFormatted, finalPriceFormatted } =
    useSubscriptionPrice({
      closedUserGroupId,
    });

  const rootClassName = cn(
    'fixed inset-x-0 top-0 z-sticky h-[88px] -translate-y-full bg-white shadow-highlightPewter transition-transform',
    {
      'bottom-0 left-0 right-0 top-auto translate-y-full':
        position === 'bottom',
      'translate-y-0': show,
    },
    className
  );

  // Delay always a second to prevent issue between desktop/mobile position
  useEffect(() => {
    setTimeout(() => {
      setLoaded(true);
    }, 1000);
  }, []);
  const productLabel = useProductLabel(vehicle, partnerDiscount);

  if (!loaded) return;

  const brandAndModel = `${vehicle.brand.id} ${vehicle.model}`;

  return (
    <div className={rootClassName} data-appid="sticky-bar">
      <div className="container">
        <div className="flex h-[88px] max-w-full items-center justify-between sm:justify-normal">
          <div className="hidden w-full flex-col gap-3 sm:flex">
            {productLabel && (
              <span className="text-trustedBlue body-14-semibold">
                {productLabel}
              </span>
            )}
            <div className="flex items-center gap-1">
              <span className="body-16-semibold">{brandAndModel}</span>
              {configLine && (
                <span className="body-16-light">{configLine}</span>
              )}
            </div>
          </div>
          <div className="mr-4 flex-col flex-wrap items-end gap-2">
            <div className="flex items-center justify-start">
              <span className="inline-block body-16-semibold">
                {i18n.formatMessage('pdp.price.starting_from')}&nbsp;
              </span>
              {originalPriceFormatted && (
                <span className="mr-2 inline-block !text-iron line-through body-16-semibold">
                  {originalPriceFormatted}
                </span>
              )}
              <span className="inline-block body-16-semibold">
                {finalPriceFormatted}
              </span>
            </div>
            <p className="mt-1.5 whitespace-nowrap body-16-light">
              {i18n.formatMessage('pdp.price.monthly')}
            </p>
          </div>
          <div onClick={PDPTracking.stickyAddToCartClicked}>
            {isAvailable && renderSubmitButton()}
          </div>
        </div>
      </div>
    </div>
  );
};
